import React, { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

type CustomColumnTextCellProps = {
  value: string;
  onCommit: (nextValue: string) => Promise<void> | void;
};

const clampTwoLinesStyle: React.CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  maxHeight: '2.5rem',
};

const MAX_PREVIEW_HEIGHT_PX = 40;

const resizeTextarea = (element: HTMLTextAreaElement | null) => {
  if (!element) {
    return;
  }
  element.style.height = 'auto';
  element.style.height = `${element.scrollHeight}px`;
};

const CustomColumnTextCell: React.FC<CustomColumnTextCellProps> = ({ value, onCommit }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draftValue, setDraftValue] = useState(value || '');
  const [displayValue, setDisplayValue] = useState(value || '');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const measureRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!isEditing) {
      setDraftValue(value || '');
    }
  }, [isEditing, value]);

  useEffect(() => {
    if (isEditing) {
      setDisplayValue(value || '');
      return;
    }

    const calculateDisplayValue = () => {
      const element = measureRef.current;
      const textValue = value || '';
      if (!element || !textValue) {
        setDisplayValue(textValue);
        return;
      }

      element.textContent = textValue;
      if (element.scrollHeight <= MAX_PREVIEW_HEIGHT_PX) {
        setDisplayValue(textValue);
        return;
      }

      let low = 0;
      let high = textValue.length;
      let best = '';

      while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        const nextValue = `${textValue.slice(0, mid).trimEnd()}...`;
        element.textContent = nextValue;

        if (element.scrollHeight <= MAX_PREVIEW_HEIGHT_PX) {
          best = nextValue;
          low = mid + 1;
        } else {
          high = mid - 1;
        }
      }

      setDisplayValue(best || '...');
    };

    const animationFrameId = window.requestAnimationFrame(calculateDisplayValue);
    const resizeObserver =
      typeof ResizeObserver !== 'undefined' && buttonRef.current
        ? new ResizeObserver(calculateDisplayValue)
        : null;

    if (resizeObserver && buttonRef.current) {
      resizeObserver.observe(buttonRef.current);
    }

    window.addEventListener('resize', calculateDisplayValue);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', calculateDisplayValue);
    };
  }, [isEditing, value]);

  useEffect(() => {
    if (!isEditing) {
      return;
    }
    resizeTextarea(textareaRef.current);
  }, [draftValue, isEditing]);

  const startEditing = () => {
    setDraftValue(value || '');
    setIsEditing(true);
  };

  const handleBlur = async () => {
    setIsEditing(false);
    if (draftValue === (value || '')) {
      return;
    }
    await onCommit(draftValue);
  };

  if (isEditing) {
    return (
      <textarea
        ref={textareaRef}
        value={draftValue}
        placeholder="Type here"
        autoFocus
        onChange={(event) => setDraftValue(event.target.value)}
        onBlur={() => void handleBlur()}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            setDraftValue(value || '');
            setIsEditing(false);
            return;
          }
          if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
            event.preventDefault();
            event.currentTarget.blur();
          }
        }}
        className="block w-full max-w-full min-w-0 min-h-[32px] resize-none overflow-hidden rounded-md border border-input bg-background px-2 py-1 text-sm leading-5 text-foreground outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
      />
    );
  }

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={startEditing}
      className="relative block w-full max-w-full min-w-0 min-h-[36px] rounded-md border border-input bg-background px-2 py-1 text-left whitespace-normal hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring overflow-hidden"
    >
      <span
        className={cn(
          'block w-full max-w-full min-w-0 text-sm leading-5 whitespace-normal break-words overflow-hidden',
          value ? 'text-foreground' : 'text-muted-foreground'
        )}
        style={clampTwoLinesStyle}
      >
        {value ? displayValue : 'Type here'}
      </span>
      <span
        ref={measureRef}
        aria-hidden="true"
        className="pointer-events-none absolute left-2 right-2 top-1 invisible block text-sm leading-5 whitespace-normal break-words"
      />
    </button>
  );
};

export default CustomColumnTextCell;
